import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Zap, ArrowLeft, Upload as UploadIcon, FileText, X, Loader2, AlertCircle, Plus, Trash2, User, Calendar, Scale, Ruler, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Upload() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [testType, setTestType] = useState("20min");
  const [athleteName, setAthleteName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Individual athlete profile state
  const [athleteProfile, setAthleteProfile] = useState(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profileForm, setProfileForm] = useState({
    birth_date: "",
    weight_kg: "",
    height_cm: "",
    gender: "male"
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Test-specific weight (can differ from profile weight)
  const [testWeight, setTestWeight] = useState("");
  
  // Test date and time
  const [testDate, setTestDate] = useState("");
  const [testTime, setTestTime] = useState("");
  
  // Laktat state
  const [showLaktatDialog, setShowLaktatDialog] = useState(false);
  const [hasLaktat, setHasLaktat] = useState(null);
  const [laktatValues, setLaktatValues] = useState([
    { watt: "", laktat: "" },
    { watt: "", laktat: "" },
    { watt: "", laktat: "" },
  ]);

  // Check for athlete name in URL params
  useEffect(() => {
    const athleteParam = searchParams.get("athlete");
    if (athleteParam) {
      setAthleteName(athleteParam);
    }
  }, [searchParams]);

  // Fetch athlete profile when name changes
  useEffect(() => {
    if (athleteName && athleteName.trim()) {
      fetchAthleteProfile(athleteName);
    } else {
      setAthleteProfile(null);
    }
  }, [athleteName]);

  useEffect(() => {
    checkRequirements();
  }, []);

  const fetchAthleteProfile = async (name) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/athletes/${encodeURIComponent(name)}`, {
        credentials: "include",
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.profile) {
          setAthleteProfile(data.profile);
          setProfileForm({
            birth_date: data.profile.birth_date || "",
            weight_kg: data.profile.weight_kg || "",
            height_cm: data.profile.height_cm || "",
            gender: data.profile.gender || "male"
          });
          // Pre-fill test weight with profile weight
          setTestWeight(data.profile.weight_kg?.toString() || "");
        } else {
          setAthleteProfile(null);
          setTestWeight("");
        }
      }
    } catch (error) {
      console.error("Error fetching athlete profile:", error);
    }
  };

  const checkRequirements = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const paymentRes = await fetch(`${API}/payment/status`, { credentials: "include", headers });

      if (paymentRes.ok) {
        const data = await paymentRes.json();
        setPaymentStatus(data);
      }
    } catch (error) {
      console.error("Error checking requirements:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveAthleteProfile = async () => {
    if (!athleteName.trim()) {
      toast.error("Bitte gib einen Athletennamen ein");
      return;
    }
    
    if (!profileForm.birth_date || !profileForm.weight_kg || !profileForm.height_cm) {
      toast.error("Bitte fülle alle Pflichtfelder aus");
      return;
    }
    
    setSavingProfile(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` })
      };
      
      const response = await fetch(`${API}/athletes`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          athlete_name: athleteName.trim(),
          birth_date: profileForm.birth_date,
          weight_kg: parseFloat(profileForm.weight_kg),
          height_cm: parseFloat(profileForm.height_cm),
          gender: profileForm.gender
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAthleteProfile(data.profile);
        setShowProfileForm(false);
        // Set test weight to the new profile weight
        setTestWeight(profileForm.weight_kg.toString());
        toast.success("Athletenprofil gespeichert!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern des Profils");
    } finally {
      setSavingProfile(false);
    }
  };

  // Helper function to format date without timezone shift
  const formatDateLocal = (dateString) => {
    if (!dateString) return "";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const date = new Date(year, month, day);
      return date.toLocaleDateString("de-DE");
    }
    return new Date(dateString).toLocaleDateString("de-DE");
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile.name.toLowerCase().endsWith(".fit")) {
      toast.error("Bitte wähle eine .FIT-Datei aus");
      return;
    }
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Bitte wähle eine Datei aus");
      return;
    }

    if (!athleteName.trim()) {
      toast.error("Bitte gib einen Athletennamen ein");
      return;
    }

    if (!athleteProfile) {
      toast.error("Bitte erstelle zuerst ein Athletenprofil");
      setShowProfileForm(true);
      return;
    }

    if (!testWeight || parseFloat(testWeight) <= 0) {
      toast.error("Bitte gib ein gültiges Gewicht ein");
      return;
    }

    if (!testDate) {
      toast.error("Bitte gib das Testdatum ein");
      return;
    }

    if (!paymentStatus?.is_active) {
      toast.error("Bitte wähle einen Zahlungsplan");
      navigate("/pricing");
      return;
    }

    // Show laktat dialog first
    setShowLaktatDialog(true);
  };

  const addLaktatRow = () => {
    setLaktatValues([...laktatValues, { watt: "", laktat: "" }]);
  };

  const removeLaktatRow = (index) => {
    if (laktatValues.length > 1) {
      setLaktatValues(laktatValues.filter((_, i) => i !== index));
    }
  };

  const updateLaktatValue = (index, field, value) => {
    const updated = [...laktatValues];
    updated[index][field] = value;
    setLaktatValues(updated);
  };

  const processUpload = async (includeLaktat) => {
    setShowLaktatDialog(false);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Prepare laktat data if included
      let laktatData = null;
      if (includeLaktat) {
        laktatData = laktatValues
          .filter(v => v.watt && v.laktat)
          .map(v => ({
            watt: parseFloat(v.watt),
            laktat: parseFloat(v.laktat)
          }))
          .sort((a, b) => a.watt - b.watt);
      }

      const queryParams = new URLSearchParams({
        test_type: testType,
        athlete_name: athleteName.trim(),
        test_weight: testWeight,  // Individual weight for this test
        test_date: testDate,  // Test date (required)
        ...(testTime && { test_time: testTime }),  // Test time (optional)
        ...(laktatData && laktatData.length > 0 && { laktat_data: JSON.stringify(laktatData) })
      });

      const response = await fetch(`${API}/analysis/upload?${queryParams}`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t('upload.success', 'Analyse erfolgreich erstellt!'));
        navigate(`/analysis/${data.analysis_id}`);
      } else {
        const error = await response.json();
        console.error("Upload error:", error);
        toast.error(error.detail || t('upload.error', 'Fehler beim Hochladen'));
      }
    } catch (error) {
      console.error("Upload network error:", error);
      toast.error(t('upload.error', 'Fehler beim Hochladen. Bitte überprüfe deine Internetverbindung und versuche es erneut.'));
    } finally {
      setUploading(false);
    }
  };

  // Calculate age from birth date for display
  const getAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    // Parse date without timezone conversion
    const parts = birthDate.split("-");
    let birth;
    if (parts.length === 3) {
      birth = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    } else {
      birth = new Date(birthDate);
    }
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-volt"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-8 w-8 text-volt" />
              <span className="font-display font-bold text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">by Loops Cycling Academy</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="minimal" />
              <Link to="/dashboard" className="nav-link text-sm font-medium flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="upload-page">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <p className="data-label mb-2">{t('upload.dropzone')}</p>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase">
              {t('upload.title').split(' ')[0]} <span className="text-volt">{t('upload.title').split(' ').slice(1).join(' ') || t('analysis.title')}</span>
            </h1>
          </div>

          {/* Requirements Check */}
          {!paymentStatus?.is_active && (
            <div className="glass-card rounded-xl p-6 mb-6 border border-carb-orange/30">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-carb-orange flex-shrink-0" />
                <div>
                  <h3 className="font-display font-bold uppercase mb-2">{t('upload.requirement', 'Voraussetzung')}</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm">
                      <div className="h-1.5 w-1.5 rounded-full bg-carb-orange"></div>
                      <Link to="/pricing" className="text-volt hover:underline">
                        {t('upload.selectPaymentPlan', 'Zahlungsplan auswählen')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Athlete Name */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <Label htmlFor="athleteName" className="data-label mb-4 block">{t('upload.athleteName')} *</Label>
            {searchParams.get("athlete") && (
              <div className="flex items-center gap-2 mb-3 text-sm text-cyan">
                <User className="h-4 w-4" />
                <span>{t('upload.newTestFor', 'Neuer Test für')}: <strong>{searchParams.get("athlete")}</strong></span>
              </div>
            )}
            <Input
              id="athleteName"
              type="text"
              value={athleteName}
              onChange={(e) => setAthleteName(e.target.value)}
              placeholder={t('upload.athleteNamePlaceholder')}
              className="bg-zinc-900 border-zinc-800 focus:border-volt h-12"
              data-testid="athlete-name-input"
            />
          </div>

          {/* Athlete Profile Section */}
          {athleteName.trim() && (
            <div className="glass-card rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="data-label">{t('upload.athleteProfile', 'Athletenprofil')}</Label>
                {athleteProfile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowProfileForm(true)}
                    className="text-volt text-xs"
                  >
                    {t('common.edit')}
                  </Button>
                )}
              </div>
              
              {athleteProfile && !showProfileForm ? (
                // Show existing profile
                <div className="bg-zinc-900/50 rounded-lg p-4 border border-volt/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Check className="h-4 w-4 text-volt" />
                    <span className="text-sm text-volt font-medium">{t('upload.profileExists', 'Profil vorhanden')}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {t('upload.birthDate', 'Geb.')}
                      </p>
                      <p className="font-medium">
                        {formatDateLocal(athleteProfile.birth_date)}
                        <span className="text-muted-foreground text-sm ml-1">
                          ({getAge(athleteProfile.birth_date)} {t('upload.years', 'J.')})
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Scale className="h-3 w-3" /> {t('upload.weight')}
                      </p>
                      <p className="font-medium">{athleteProfile.weight_kg} kg</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Ruler className="h-3 w-3" /> {t('upload.height')}
                      </p>
                      <p className="font-medium">{athleteProfile.height_cm} cm</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('upload.gender')}</p>
                      <p className="font-medium">{athleteProfile.gender === "male" ? t('upload.genderMale') : t('upload.genderFemale')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                // Show profile form
                <div className="space-y-4">
                  {!athleteProfile && (
                    <div className="flex items-center gap-2 text-sm text-carb-orange mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <span>{t('upload.noProfile', { name: athleteName })}</span>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="birthDate" className="text-xs text-muted-foreground mb-1 block">{t('upload.birthDateFull')} *</Label>
                      <Input
                        id="birthDate"
                        type="date"
                        value={profileForm.birth_date}
                        onChange={(e) => setProfileForm({...profileForm, birth_date: e.target.value})}
                        className="bg-zinc-900 border-zinc-800 focus:border-volt"
                        data-testid="profile-birth-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender" className="text-xs text-muted-foreground mb-1 block">{t('upload.gender')}</Label>
                      <select
                        id="gender"
                        value={profileForm.gender}
                        onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                        className="w-full h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-md focus:border-volt focus:outline-none"
                        data-testid="profile-gender"
                      >
                        <option value="male">{t('upload.genderMale')}</option>
                        <option value="female">{t('upload.genderFemale')}</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="weight" className="text-xs text-muted-foreground mb-1 block">{t('upload.weightKg')} *</Label>
                      <Input
                        id="weight"
                        type="number"
                        step="0.1"
                        value={profileForm.weight_kg}
                        onChange={(e) => setProfileForm({...profileForm, weight_kg: e.target.value})}
                        placeholder="75"
                        className="bg-zinc-900 border-zinc-800 focus:border-volt"
                        data-testid="profile-weight"
                      />
                    </div>
                    <div>
                      <Label htmlFor="height" className="text-xs text-muted-foreground mb-1 block">{t('upload.heightCm')} *</Label>
                      <Input
                        id="height"
                        type="number"
                        value={profileForm.height_cm}
                        onChange={(e) => setProfileForm({...profileForm, height_cm: e.target.value})}
                        placeholder="180"
                        className="bg-zinc-900 border-zinc-800 focus:border-volt"
                        data-testid="profile-height"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={saveAthleteProfile}
                      disabled={savingProfile}
                      className="bg-volt text-black hover:bg-volt/90 font-bold"
                      data-testid="save-profile-btn"
                    >
                      {savingProfile ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      {t('upload.saveProfile')}
                    </Button>
                    {athleteProfile && (
                      <Button
                        variant="outline"
                        onClick={() => setShowProfileForm(false)}
                        className="border-zinc-700"
                      >
                        {t('common.cancel')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Test Type Selection */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <Label className="data-label mb-4 block">{t('upload.testType')}</Label>
            <RadioGroup value={testType} onValueChange={setTestType} className="space-y-3">
              <div className="flex items-center space-x-3 p-4 rounded-lg bg-zinc-900/50 border border-white/5 hover:border-volt/30 transition-colors cursor-pointer">
                <RadioGroupItem value="20min" id="20min" data-testid="test-type-20min" />
                <div className="flex-1">
                  <Label htmlFor="20min" className="font-medium cursor-pointer">{t('upload.testType20min')}</Label>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 rounded-lg bg-zinc-900/50 border border-white/5 hover:border-volt/30 transition-colors cursor-pointer">
                <RadioGroupItem value="stufentest" id="stufentest" data-testid="test-type-stufentest" />
                <div className="flex-1">
                  <Label htmlFor="stufentest" className="font-medium cursor-pointer">{t('upload.testTypeRamp')}</Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Test Weight - Individual per test */}
          {athleteProfile && (
            <div className="glass-card rounded-xl p-6 mb-6">
              <Label htmlFor="testWeight" className="data-label mb-2 block">{t('upload.currentWeightForTest')}</Label>
              <p className="text-xs text-muted-foreground mb-4">
                Das Gewicht kann für jeden Test individuell angepasst werden (Profil: {athleteProfile.weight_kg} kg)
              </p>
              <div className="flex items-center gap-3">
                <Input
                  id="testWeight"
                  type="number"
                  step="0.1"
                  value={testWeight}
                  onChange={(e) => setTestWeight(e.target.value)}
                  placeholder={athleteProfile.weight_kg?.toString()}
                  className="bg-zinc-900 border-zinc-800 focus:border-volt h-12 w-32"
                  data-testid="test-weight-input"
                />
                <span className="text-muted-foreground">kg</span>
                {testWeight && parseFloat(testWeight) !== athleteProfile.weight_kg && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTestWeight(athleteProfile.weight_kg?.toString() || "")}
                    className="text-xs text-muted-foreground hover:text-white"
                  >
                    Zurücksetzen
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Test Date and Time */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Test Date - Required */}
              <div>
                <Label htmlFor="testDate" className="data-label mb-2 block">
                  Testdatum <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Wann wurde der Test durchgeführt?
                </p>
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-volt" />
                  <Input
                    id="testDate"
                    type="date"
                    value={testDate}
                    onChange={(e) => setTestDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="bg-zinc-900 border-zinc-800 focus:border-volt h-12 flex-1"
                    data-testid="test-date-input"
                    required
                  />
                </div>
              </div>

              {/* Test Time - Optional */}
              <div>
                <Label htmlFor="testTime" className="data-label mb-2 block">
                  Testuhrzeit <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Um welche Uhrzeit wurde der Test gestartet?
                </p>
                <div className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-cyan" />
                  <Input
                    id="testTime"
                    type="time"
                    value={testTime}
                    onChange={(e) => setTestTime(e.target.value)}
                    className="bg-zinc-900 border-zinc-800 focus:border-volt h-12 flex-1"
                    data-testid="test-time-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="glass-card rounded-xl p-6">
            <Label className="data-label mb-4 block">FIT-Datei</Label>
            
            <div
              className={`dropzone rounded-xl p-12 text-center ${dragActive ? "active" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".fit"
                onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="flex items-center justify-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-volt/10 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-volt" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    data-testid="remove-file-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <UploadIcon className="h-12 w-12 text-volt/50 mx-auto mb-4" />
                  <p className="font-medium mb-2">Datei hierher ziehen</p>
                  <p className="text-sm text-muted-foreground mb-4">oder klicken zum Auswählen</p>
                  <p className="text-xs text-muted-foreground">Nur .FIT-Dateien</p>
                </>
              )}
            </div>

            <Button
              onClick={handleUpload}
              disabled={!file || uploading || !athleteName.trim() || !athleteProfile || !paymentStatus?.is_active}
              className="w-full mt-6 skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider h-12"
              data-testid="start-analysis-btn"
            >
              <span className="flex items-center gap-2">
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyse läuft...
                  </>
                ) : (
                  <>
                    <UploadIcon className="h-4 w-4" />
                    Analyse starten
                  </>
                )}
              </span>
            </Button>
            
            {/* Validation hints */}
            {(!athleteName.trim() || !athleteProfile || !file) && (
              <div className="mt-4 text-sm text-muted-foreground space-y-1">
                {!athleteName.trim() && <p>• Athletenname eingeben</p>}
                {athleteName.trim() && !athleteProfile && <p>• Athletenprofil erstellen</p>}
                {!file && <p>• FIT-Datei auswählen</p>}
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Die .FIT-Datei wird von deinem Fahrradcomputer oder Trainings-App exportiert.
            </p>
          </div>
        </div>
      </main>

      {/* Laktat Dialog */}
      <Dialog open={showLaktatDialog} onOpenChange={setShowLaktatDialog}>
        <DialogContent className="sm:max-w-lg bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Laktatwerte vorhanden?</DialogTitle>
            <DialogDescription>
              Wurden während des Tests Laktatwerte gemessen?
            </DialogDescription>
          </DialogHeader>

          {hasLaktat === null ? (
            <div className="flex gap-4 py-4">
              <Button
                onClick={() => setHasLaktat(true)}
                className="flex-1 bg-volt text-black hover:bg-volt/90 font-bold"
                data-testid="laktat-yes-btn"
              >
                Ja
              </Button>
              <Button
                onClick={() => processUpload(false)}
                variant="outline"
                className="flex-1 border-zinc-700"
                data-testid="laktat-no-btn"
              >
                Nein
              </Button>
            </div>
          ) : (
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Gib die gemessenen Laktatwerte ein (Watt und mmol/L):
              </p>
              
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {laktatValues.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="Watt"
                        value={value.watt}
                        onChange={(e) => updateLaktatValue(index, "watt", e.target.value)}
                        className="bg-zinc-900 border-zinc-800 h-10"
                        data-testid={`laktat-watt-${index}`}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="mmol/L"
                        value={value.laktat}
                        onChange={(e) => updateLaktatValue(index, "laktat", e.target.value)}
                        className="bg-zinc-900 border-zinc-800 h-10"
                        data-testid={`laktat-value-${index}`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLaktatRow(index)}
                      className="text-muted-foreground hover:text-red-500 px-2"
                      disabled={laktatValues.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLaktatRow}
                className="mt-3 border-zinc-700"
                data-testid="add-laktat-row-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Weitere Messung hinzufügen
              </Button>

              <DialogFooter className="mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setHasLaktat(null);
                    setShowLaktatDialog(false);
                  }}
                  className="border-zinc-700"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={() => processUpload(true)}
                  className="bg-volt text-black hover:bg-volt/90 font-bold"
                  data-testid="submit-laktat-btn"
                >
                  Analyse starten
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
